import { useState, useEffect, useCallback } from "react";
import { authenticatedBitswanBackendInstance } from "@/lib/api-client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Trash2, Shield, Loader2, Crown, Edit3 } from "lucide-react";

interface Member {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  is_admin: boolean;
}

const ROLES = [
  { value: 'owner', label: 'Owner', description: 'Runs the organisation: billing, settings, and who is in it', icon: Crown, color: 'text-amber-600' },
  { value: 'member', label: 'Member', description: 'Works on clients, specifications and offers', icon: Edit3, color: 'text-blue-600' },
];

export default function TeamPage() {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    try {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get("/advisor/organizations/members/");
      setMembers(res.data);
    } catch (e) {
      console.error("Failed to fetch members:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleInvite = async () => {
    if (!inviteEmail.trim() || inviting) return;
    setInviting(true);
    setInviteResult(null);
    try {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.post("/advisor/organizations/members/", {
        email: inviteEmail.trim(),
        message: inviteMessage.trim() || undefined,
      });
      setInviteResult({
        type: "success",
        message: res.data.temporary_password
          ? `${"User created with temporary password"}: ${res.data.temporary_password}`
          : `Invited ${inviteEmail.trim()}`,
      });
      setInviteEmail("");
      setInviteMessage("");
      await fetchMembers();
    } catch (e: any) {
      setInviteResult({
        type: "error",
        message: e.response?.data?.error || "Failed to send invitation",
      });
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (userId: string) => {
    setRemovingId(userId);
    try {
      const api = await authenticatedBitswanBackendInstance();
      await api.delete(`/advisor/organizations/members/${userId}/`);
      setMembers((prev) => prev.filter((m) => m.id !== userId));
    } catch (e) {
      console.error("Failed to remove member:", e);
    } finally {
      setRemovingId(null);
    }
  };

  const getRoleInfo = (member: Member) => {
    if (member.is_admin) return ROLES[0];
    return ROLES[1];
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Invite Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {"Invite Advisor"}
          </CardTitle>
          <CardDescription>{"Invite another advisor to join your organization by email"}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder={"advisor@example.com"}
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleInvite()}
              data-testid="invite-email"
              className="max-w-sm"
            />
            <Button
              onClick={handleInvite}
              disabled={inviting || !inviteEmail.trim()}
              data-testid="invite-submit"
            >
              {inviting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send Invite
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            New members join as members. Owners are the people in the organisation's admin group.
          </p>
          {/* Optional message */}
          <Input
            placeholder="Optional message (e.g. 'Can you help with the Brno departments?')"
            value={inviteMessage}
            onChange={(e) => setInviteMessage(e.target.value)}
            data-testid="invite-message"
            className="max-w-lg"
          />
          {inviteResult && (
            <p
              className={`text-sm ${inviteResult.type === "success" ? "text-green-600" : "text-red-600"}`}
              data-testid="invite-result"
            >
              {inviteResult.message}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Members List */}
      <Card>
        <CardHeader>
          <CardTitle>{"Team Members"}</CardTitle>
          <CardDescription>
            {members.length} member{members.length !== 1 ? 's' : ''} in your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {"Loading..."}
            </div>
          ) : members.length === 0 ? (
            <p className="text-muted-foreground">{"No members found"}</p>
          ) : (
            <div className="space-y-2" data-testid="members-list">
              {members.map((member) => {
                const role = getRoleInfo(member);
                const RoleIcon = role.icon;
                return (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                    data-testid={`member-${member.id}`}
                    data-member-email={member.email}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
                        <span className="text-sm font-medium">
                          {(member.firstName?.[0] || member.email[0] || '?').toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">
                          {member.firstName || member.lastName
                            ? `${member.firstName} ${member.lastName}`.trim()
                            : member.email}
                        </p>
                        <p className="text-sm text-muted-foreground">{member.email}</p>
                      </div>
                      <Badge variant="outline" className={`gap-1 ${role.color}`} data-testid={`role-badge-${member.id}`}>
                        <RoleIcon className="h-3 w-3" />
                        {role.label}
                      </Badge>
                    </div>
                    {!member.is_admin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(member.id)}
                        disabled={removingId === member.id}
                        data-testid={`remove-${member.id}`}
                        className="text-destructive hover:text-destructive"
                      >
                        {removingId === member.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
